'use strict';

const Fetcher = require('od-modules/utils/fetcher');
const bookingDetailUtils = require('components/user_area/my_trips/js/utils/booking_detail_utils');
const openTicketRepository = require('od-modules/open_ticket_redemption_flow/openticket_repository');
const logger = require('od-modules/utils/logger');
const {
    BIM_STATUS_NAME_GROUPS,
    BIM_CANCELLATION_STATUS,
    CARRIER_CODES,
    MANUAL_REFUND_CLASSIFICATIONS,
    OPEN_TICKET_API_STATUS,
    OPEN_TICKET_PNR_STATUS,
    BIM_CANCELLATION_RESOLUTION_TYPE,
    BIM_CANCELLATION_CLASSIFICATION_TYPE,
    OPEN_TICKET_LIST_CONFIG,
} = require('./utils/constants');
const {PROVIDERS} = require('od-modules/utils/provider_rules');

const argsValidator = (status, pnrs) => Object.keys(OPEN_TICKET_PNR_STATUS).includes(status) && !Odigeo._.isEmpty(pnrs);

module.exports = Backbone.Model.extend({
    defaults: {
        bookingInfo: {},
        cancelledPNRs: {},
        openTicketPNRs: {},
        cashRefundPNRs: {},
        involuntaryCancellationPNRs: {},
        incidents: [],
        acceptedPNRs: [],
        pendingPNRs: [],
        rejectedPNRs: [],
        redemptionPendingPNRs: [],
        bookingID: null,
        trackingInfo: {},
        contactAirlineAirlineInitDateVY: '',
        messages: [],
        avoidReload: false,
    },

    requestBookingIncidents() {
        return this.requestFlightIncidents()
            .catch(() => [])
            .then((incidents) => this.processAllIncidents(incidents))
            .then(() => this.checkAcceptedOpenTicketRedemptionStatus())
            .then(() => this.addPendingPNRsDestAvailableData())
            .then(() => this.trigger('renderCancellations'));
    },

    url(data, isBIM) {
        return Odigeo.Settings.getProperty(`${isBIM ? 'biMessagesServiceUrlFormatter' : 'flightIncidentServiceUrlFormatter'}`)(data);
    },

    requestFlightIncidents() {
        return Fetcher.get(this.url(`incidents?bookingId=${this.get('bookingID')}`)).json();
    },

    getBIMessages() {
        const {buyer: {mail} = {}} = this.get('bookingInfo') || {};
        return Fetcher.get(this.url(`?bookingId=${this.get('bookingID')}&email=${mail}`, true)).json();
    },

    updateConsent(incident, updateConsentBIM) {
        const {consent, incidentId, id: {id} = {}} = incident || {};
        const selectedId = this.get('isBIM') ? incidentId : id;
        if (!updateConsentBIM || !selectedId || !consent) {
            return Promise.reject();
        }
        incident.consent = {...consent, ...updateConsentBIM};
        return Fetcher.put(this.url(`incidents/${selectedId}/consent`), incident.consent).json();
    },

    reclassificationBIM() {
        this.getBIMessages()
            .then((messages) => this.processMessages(messages))
            .then(() => this.trigger('renderCancellations'));
    },

    processMessages(messages = []) {
        this.updateBookingInfoInSession(messages);
        this.updateModelWithNewMessages(messages);
        this.setCancellationsDataFromMessages();
        return this.checkAcceptedOpenTicketRedemptionStatus()
            .then(() => this.addPendingPNRsDestAvailableData());
    },

    updateBookingInfoInSession(messages) {
        const bookingInfo = Odigeo.Utils.parseJson(Odigeo.Session.get('bookingInfo'));
        bookingInfo.messages = messages;
        Odigeo.Session.set('bookingInfo', JSON.stringify(bookingInfo));
    },

    updateModelWithNewMessages(messages) {
        const bookingInfo = {...this.get('bookingInfo'), ...{messages: messages}};
        bookingDetailUtils.extendBookingDetailWithFlightStatusData(bookingInfo, true);
        this.set({
            bookingInfo,
            cancelledPNRs: bookingInfo.cancelledPNRs,
            messages: bookingInfo.messages,
        });
    },

    setCancellationsDataFromMessages() {
        // TODO: Move to view when redemptionPNRs to be migrated
        this.set('involuntaryCancellationPNRs', this.getCancelledPNRsByGroup(BIM_STATUS_NAME_GROUPS.GENERAL_INVOLUNTARY));
        this.set('cashRefundPNRs', this.getCancelledPNRsByGroup(BIM_STATUS_NAME_GROUPS.CASH_REFUND));
        this.set('openTicketPNRs', this.getCancelledPNRsByGroup(BIM_STATUS_NAME_GROUPS.OPEN_TICKET));
        this.set('acceptedPNRs', this.getOpenTicketByType(BIM_CANCELLATION_STATUS.OPEN_TICKET_ACCEPTED));
        this.set('pendingPNRs', this.getOpenTicketByType(BIM_CANCELLATION_STATUS.OPEN_TICKET_PENDING));
        this.set('redemptionPendingPNRs', this.getOpenTicketByType(BIM_CANCELLATION_STATUS.REDEMPTION_PENDING));
    },

    getCancelledPNRsByGroup(group) {
        const cancelledPNRs = this.get('cancelledPNRs');
        return Object.keys(cancelledPNRs)
            .filter((key) => cancelledPNRs[key].statusGroup === group)
            .reduce((PNRsByType, key) => {
                PNRsByType[key] = cancelledPNRs[key];
                return PNRsByType;
            }, {});
    },

    getOpenTicketByType(status) {
        const openTicketPNRs = this.get('openTicketPNRs');
        return Object.keys(openTicketPNRs).filter((key) => openTicketPNRs[key].cancellationStatus === status);
    },

    getInfoPNRs(cancellations) {
        const cancelledPNRs = this.get('cancelledPNRs');
        return cancellations.reduce((infoPNRs, {pnr, cancellationStatus}) => {
            if (BIM_CANCELLATION_STATUS.REFUND_NOT_MANAGED_BY_EDO === cancellationStatus && this.isValidVuelingPNR(cancelledPNRs[pnr])) {
                cancelledPNRs[pnr].cancellationStatus = BIM_CANCELLATION_STATUS.CONTACT_AIRLINE;
            }
            infoPNRs[pnr] = cancelledPNRs[pnr];
            return infoPNRs;
        }, {});
    },

    addPendingPNRsDestAvailableData() {
        const pendingPNRs = this.get('pendingPNRs');
        const bookingId = this.get('bookingID');
        const openTicketPNRs = this.get('openTicketPNRs');
        const promises = pendingPNRs.map((pnr) => {
            const bookingItemId = this.get('openTicketPNRs')[pnr].sections[0].itineraryBookingId;

            return openTicketRepository(Fetcher, Odigeo.Settings.getProperty('bookingChangeRequestUrlFormatter'))
                .getRedemptionConditions({bookingId, bookingItemId})
                .then((redemptionConditions) => {
                    openTicketPNRs[pnr].changeRouteAvailable = redemptionConditions.changeRouteAvailable;
                })
                .catch((err) => {
                    openTicketPNRs[pnr].changeRouteAvailable = false;
                    logger.error(err, 'Failed to retrieve Redemption Conditions changeRouteAvailable');
                });
        });
        return Promise.all(promises);
    },

    processAllIncidents(incidents) {
        this.set('incidents', incidents || []);
        const openTicketPNRs = {};
        const cashRefundPNRs = {};
        const involuntaryCancellationPNRs = {};
        Object.entries(this.get('cancelledPNRs')).forEach(([pnr, pnrInfo]) => {
            const {openTicketSections, cashRefundSections, involuntaryCancellationSections} = this.splitPNRSections(pnrInfo, pnr);
            if (!Odigeo._.isEmpty(openTicketSections)) {
                openTicketPNRs[pnr] = Object.assign({}, pnrInfo, {sections: openTicketSections});
            }
            if (!Odigeo._.isEmpty(cashRefundSections)) {
                cashRefundPNRs[pnr] = Object.assign({}, pnrInfo, {sections: cashRefundSections});
            }
            if (!Odigeo._.isEmpty(involuntaryCancellationSections)) {
                involuntaryCancellationPNRs[pnr] = Object.assign({}, pnrInfo, {sections: involuntaryCancellationSections});
            }
        });
        this.set({
            openTicketPNRs,
            cashRefundPNRs,
            involuntaryCancellationPNRs,
        });
    },

    splitPNRSections(pnrInfo, pnr) {
        const resultingSections = {
            openTicketSections: [],
            cashRefundSections: [],
            involuntaryCancellationSections: [],
        };
        return pnrInfo.sections.reduce(
            (acc, section) => {
                const incidentMatched = this.getIncidentByProviderBookingItemId(section.itineraryBookingId);
                if (this._isPendingClassification(pnrInfo, incidentMatched)) {
                    pnrInfo.cancellationStatus = BIM_CANCELLATION_STATUS.PENDING_CLASSIFICATION;
                    pnrInfo.extraHeader = true;
                    acc.involuntaryCancellationSections.push(section);
                } else if (this._isBankruptcyAirline(incidentMatched)) {
                    pnrInfo.cancellationStatus = BIM_CANCELLATION_STATUS.BANKRUPTCY;
                    acc.involuntaryCancellationSections.push(section);
                } else if (!this._isDiscarded(incidentMatched)) {
                    pnrInfo.refundStatus = incidentMatched.cashRefundStatus;
                    if (this._isRefundNotManagedByEdo(incidentMatched, pnrInfo)) {
                        pnrInfo.cancellationStatus = this.isValidVuelingPNR(pnrInfo) ?
                            BIM_CANCELLATION_STATUS.CONTACT_AIRLINE :
                            BIM_CANCELLATION_STATUS.REFUND_NOT_MANAGED_BY_EDO;
                        acc.involuntaryCancellationSections.push(section);
                    } else if (this._isResolved(incidentMatched)) {
                        pnrInfo.cancellationStatus = BIM_CANCELLATION_STATUS.RESOLVED;
                    } else if (this._isResolvedByCustomer(incidentMatched)) {
                        pnrInfo.cancellationStatus = BIM_CANCELLATION_STATUS.RESOLVED_BY_CUSTOMER;
                    } else if (this._isManageByTouchless(incidentMatched)) {
                        acc.involuntaryCancellationSections.push(section);
                        pnrInfo.cancellationStatus = BIM_CANCELLATION_STATUS.MANAGED_BY_TOUCHLESS;
                    } else if (this._isManagedByICM(incidentMatched)) {
                        acc.involuntaryCancellationSections.push(section);
                        pnrInfo.cancellationStatus = BIM_CANCELLATION_STATUS.MANAGED_BY_ICM;
                    } else if (this._isFlexibleTicket(incidentMatched)) {
                        const flexibleTicketStatus = Odigeo._.get(incidentMatched, 'consent.flexibleTicketStatus');
                        pnrInfo.incidentInfo = this.addIncidentInfo(section.itineraryBookingId);
                        this.addCancellationProviderItemInfo(flexibleTicketStatus, pnrInfo, incidentMatched);
                        this.addOpenTicketPNRs(flexibleTicketStatus, pnr);
                        pnrInfo.flexibleTicketStatus = flexibleTicketStatus;
                        pnrInfo.cancellationStatus = BIM_CANCELLATION_STATUS['OPEN_TICKET_' + flexibleTicketStatus];
                        acc.openTicketSections.push(section);
                    } else if (this._isRefundAlternatives(incidentMatched)) {
                        pnrInfo.cancellationStatus = BIM_CANCELLATION_STATUS.REFUND_WAITING_CONSENT;
                        acc.involuntaryCancellationSections.push(section);
                    } else if (this._isRefund(incidentMatched)) {
                        if (incidentMatched.resolution.type === BIM_CANCELLATION_RESOLUTION_TYPE.MANUAL) {
                            pnrInfo.manualRefund = MANUAL_REFUND_CLASSIFICATIONS.includes(incidentMatched.resolution.classification);
                        } else {
                            pnrInfo.manualRefund = false;
                        }
                        if ((Odigeo._.get(incidentMatched, 'consent.airlineAlternativeAccepted', null) !== false) ||
                            (Odigeo._.get(incidentMatched, 'consent.cashRefundAccepted', null) !== true)) {
                            pnrInfo.extraHeader = true;
                        }
                        pnrInfo.cancellationStatus = BIM_CANCELLATION_STATUS.REFUND;
                        acc.cashRefundSections.push(section);
                    } else {
                        pnrInfo.cancellationStatus = BIM_CANCELLATION_STATUS.PENDING_CLASSIFICATION;
                        pnrInfo.extraHeader = true;
                        acc.involuntaryCancellationSections.push(section);
                    }
                }
                return acc;
            },
            resultingSections,
        );
    },

    _isFlexibleTicket(incidentMatched) {
        return incidentMatched.flexibleTicket;
    },

    _isDiscarded(incidentMatched) {
        return incidentMatched.discarded;
    },

    _isRefund(incidentMatched) {
        return (incidentMatched.resolution.type === BIM_CANCELLATION_RESOLUTION_TYPE.BSP_LINK ||
            (incidentMatched.resolution.type === BIM_CANCELLATION_RESOLUTION_TYPE.MANUAL &&
                MANUAL_REFUND_CLASSIFICATIONS.includes(incidentMatched.resolution.classification)));
    },

    _isManageByTouchless(incidentMatched) {
        return incidentMatched.resolution.type === BIM_CANCELLATION_RESOLUTION_TYPE.MANAGED_BY_TOUCHLESS;
    },

    _isManagedByICM({resolution: {type}}) {
        return type === BIM_CANCELLATION_RESOLUTION_TYPE.MANAGED_BY_ICM;
    },

    _isRefundAlternatives(incidentMatched) {
        return incidentMatched.resolution.type === BIM_CANCELLATION_RESOLUTION_TYPE.REFUND_WAITING_CONSENT;
    },

    _isRefundNotManagedByEdo(incidentMatched, pnrInfo) {
        return incidentMatched.resolution.type === BIM_CANCELLATION_RESOLUTION_TYPE.REFUND_NOT_MANAGED_BY_EDO ||
            [CARRIER_CODES.RYANAIR, CARRIER_CODES.LAUDAMOTION].includes(pnrInfo.validatingCarrier.code);
    },

    _isResolved({resolution} = {}) {
        const {type, classification} = resolution;
        return (type === BIM_CANCELLATION_RESOLUTION_TYPE.RESOLVED) ||
            (type === BIM_CANCELLATION_RESOLUTION_TYPE.MANUAL && classification === BIM_CANCELLATION_CLASSIFICATION_TYPE.RESOLVED);
    },

    _isResolvedByCustomer(incident) {
        return incident.resolution.type === BIM_CANCELLATION_RESOLUTION_TYPE.RESOLVED_BY_CUSTOMER;
    },

    _isIncidentCreated(incidentMatched) {
        return (!Odigeo._.isUndefined(incidentMatched));
    },

    _isBankruptcyAirline(incidentMatched) {
        return incidentMatched.resolution.classification === BIM_CANCELLATION_STATUS.BANKRUPTCY;
    },

    _isPendingClassification(pnrInfo, incidentMatched) {
        const isIncidentCreated = this._isIncidentCreated(incidentMatched);
        return !isIncidentCreated
            || (incidentMatched.resolution.type === BIM_CANCELLATION_RESOLUTION_TYPE.PENDING_CLASSIFICATION);
    },

    isValidVuelingPNR({provider, validatingCarrier: {code}}) {
        const isVYProvider = (provider === PROVIDERS.VUELING);
        const isTFProviderVYCarrier = (provider === PROVIDERS.TRAVELFUSION) && (code === CARRIER_CODES.VUELING);
        return isVYProvider || isTFProviderVYCarrier;
    },

    addIncidentInfo(itineraryBookingId) {
        const incidentMatched = this.getOldestIncidentByProviderBookingItemId(itineraryBookingId);
        const {creationDateTime, departureLocalDateTime, source} = incidentMatched;
        return {
            creationDateTime: Odigeo.Utils.fromLocalDateTimeToMillisecs(creationDateTime),
            departureLocalDateTime: departureLocalDateTime ? Odigeo.Utils.fromLocalDateTimeToMillisecs(departureLocalDateTime) : null,
            source,
        };
    },

    addCancellationProviderItemInfo(flexibleTicketStatus, pnrInfo, incident) {
        if (flexibleTicketStatus !== OPEN_TICKET_PNR_STATUS.REJECTED) {
            const {openTicketTotalAmount, topUpVoucherAmount, primeSubscriptionAmount, cashRefundAmount} = incident.cancellationProviderItem || {};
            const formattedCancellationProviderItem = Odigeo._.mapValues(
                {openTicketTotalAmount, topUpVoucherAmount, primeSubscriptionAmount, cashRefundAmount},
                (providerItem) => {
                    providerItem = providerItem || {};
                    providerItem.currencyConfig = Object.assign(providerItem.currencyConfig || {}, {decimalPlaces: 0});
                    return Odigeo._.isNull(providerItem.amount) || Odigeo._.isUndefined(providerItem.amount)
                        ? ''
                        : bookingDetailUtils.formatPrice(providerItem);
                });
            Object.assign(pnrInfo, formattedCancellationProviderItem);
            if (Odigeo._.isEmpty(formattedCancellationProviderItem.openTicketTotalAmount) ||
                Odigeo._.isEmpty(formattedCancellationProviderItem.topUpVoucherAmount) ||
                Odigeo._.isEmpty(formattedCancellationProviderItem.cashRefundAmount)) {
                this.trigger('trackInvalidCancellationProviderItem', incident.id);
            }
        }
    },

    checkAcceptedOpenTicketRedemptionStatus() {
        const cancelledPNRs = this.get('cancelledPNRs');
        const acceptedPNRs = this.get('acceptedPNRs');
        const openTicketPNRs = this.get('openTicketPNRs');
        const redemptionPendingPNRs = this.get('redemptionPendingPNRs');
        const redemptionStatusPromises = acceptedPNRs.map((pnr) => this.requestRedemptionStatusForPNR(pnr).catch(() => {
            return {};
        }));

        return Promise.all(redemptionStatusPromises).then((redemptionStatusResponseList) => {
            redemptionStatusResponseList
                .filter((response) => Object.keys(response).length > 0)
                .forEach((openTicketStatus, index) => {
                    const pnr = acceptedPNRs[index];
                    openTicketPNRs[pnr].openTicketStatus = openTicketStatus;
                    if (openTicketStatus === OPEN_TICKET_API_STATUS.PROCESSING_REDEMPTION) {
                        openTicketPNRs[pnr].flexibleTicketStatus = OPEN_TICKET_PNR_STATUS.REDEMPTION_PENDING;
                        openTicketPNRs[pnr].cancellationStatus = BIM_CANCELLATION_STATUS.REDEMPTION_PENDING;
                        cancelledPNRs[pnr].cancellationStatus = BIM_CANCELLATION_STATUS.REDEMPTION_PENDING;
                        acceptedPNRs.splice(index, 1);
                        redemptionPendingPNRs.push(pnr);
                    }
                });
        });
    },

    requestRedemptionStatusForPNR(pnr) {
        const bookingId = this.get('bookingID');
        const bookingItemId = this.get('openTicketPNRs')[pnr].sections[0].itineraryBookingId;
        return openTicketRepository(Fetcher, Odigeo.Settings.getProperty('bookingChangeRequestUrlFormatter'))
            .getOpenTicketStatus({bookingId, bookingItemId});
    },

    addOpenTicketPNRs(status, ...pnrs) {
        if (argsValidator(status, pnrs)) {
            const pnrList = this.getOpenTicketPNRList(status);
            pnrs.filter((pnr) => pnrList.indexOf(pnr) === -1).forEach((pnr) => pnrList.push(pnr));
        }
    },

    removeOpenTicketPNR(status, pnr) {
        if (argsValidator(status, pnr)) {
            const pnrList = this.getOpenTicketPNRList(status);
            const index = pnrList.indexOf(pnr);
            if (index !== -1) {
                pnrList.splice(index, 1);
            }
        }
    },

    persistReclassification(pnr, newCancellationStatus, updateConsentBIM) {
        const cancelledPNRs = this.get('cancelledPNRs');
        const pnrInfo = cancelledPNRs[pnr];
        const providerBookingItemId = Odigeo._.get(pnrInfo, 'sections[0].itineraryBookingId');
        const currentCancellationStatus = pnrInfo.cancellationStatus;
        const incident = this.getIncidentByProviderBookingItemId(parseInt(providerBookingItemId));

        if (currentCancellationStatus === BIM_CANCELLATION_STATUS.REFUND_WAITING_CONSENT) {
            const involuntaryCancellationPNRs = this.get('involuntaryCancellationPNRs');
            delete involuntaryCancellationPNRs[pnr];
        } else {
            this.removeOpenTicketPNR(OPEN_TICKET_PNR_STATUS.PENDING, pnr);
        }

        pnrInfo.cancellationStatus = newCancellationStatus;

        this.updateConsent(incident, updateConsentBIM);
        if ([BIM_CANCELLATION_STATUS.OPEN_TICKET_ACCEPTED, BIM_CANCELLATION_STATUS.OPEN_TICKET_REJECTED].includes(newCancellationStatus)) {
            const newOTStatus = OPEN_TICKET_PNR_STATUS[newCancellationStatus.substring(3)];
            const openTicketPNRs = this.get('openTicketPNRs');
            pnrInfo.flexibleTicketStatus = newOTStatus;
            openTicketPNRs[pnr] = pnrInfo;
            this.addOpenTicketPNRs(newOTStatus, pnr);
        } else if (newCancellationStatus === BIM_CANCELLATION_STATUS.REFUND) {
            pnrInfo.extraHeader = false;
            const cashRefundPNRs = this.get('cashRefundPNRs');
            cashRefundPNRs[pnr] = pnrInfo;
        }
    },

    reset() {
        this.set('acceptedPNRs', []);
        this.set('pendingPNRs', []);
        this.set('rejectedPNRs', []);
        this.set('redemptionPendingPNRs', []);
        this.set('notificationsDisplayed', []);
    },

    getIncidentByProviderBookingItemId(providerBookingItemId) {
        const matchingIncidents = this.get('incidents').filter((i) => i.providerBookingItemId === providerBookingItemId);
        return matchingIncidents.find((incident) => incident.cancellationId !== null) || matchingIncidents[0];
    },

    getOldestIncidentByProviderBookingItemId(providerBookingItemId) {
        return this.get('incidents').filter((incident) => incident.providerBookingItemId === providerBookingItemId)
            .reduce((acc, current) => {
                const accMillis = Odigeo.Utils.fromLocalDateTimeToMillisecs(acc.creationDateTime);
                const currentMillis = Odigeo.Utils.fromLocalDateTimeToMillisecs(current.creationDateTime);
                return accMillis < currentMillis ? acc : current;
            });
    },

    getOpenTicketPNRList(status) {
        const selected = OPEN_TICKET_LIST_CONFIG[status];
        return selected ? this.get(selected) : [];
    },
});
